import { ldJson, imageObjectJsonLd } from "@/lib/seo-schema";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageToc } from "@/components/PageToc";
import { CITIES, SITE } from "@/lib/site-data";
import { cityKeywords } from "@/lib/city-keywords";
import { CTABand } from "@/components/CTA";
import { CrossLinkModule } from "@/components/CrossLinks";
import { LIMO_SLUGS } from "@/lib/commercial-landing";
import { CityLocalBlock } from "@/components/CityLocalBlock";
import { CityStaysDestinations } from "@/components/CityStaysDestinations";
import { cityStaysFaqs } from "@/lib/city-stays";
import { cityFaqList, cityFaqSchema } from "@/lib/city-faq";
import { buildCityAirportFaqs } from "@/lib/city-airport-faq";
import { HourlyRates } from "@/components/HourlyRates";
import { LocalMap } from "@/components/LocalMap";
import { CITY_COORDS } from "@/lib/city-coords";
import { Plane, Car, Users, Briefcase, Wine, Heart, Trophy, Sparkles } from "lucide-react";
import {
  CITY_FLEET_IMAGE_PATH,
  CITY_FLEET_IMAGE_WIDTH,
  CITY_FLEET_IMAGE_HEIGHT,
  cityFleetImageCaption,
  cityFleetImageSchema,
} from "@/lib/city-fleet-image";
import { InstantPriceWidget } from "@/components/InstantPriceWidget";
import { breadcrumbListSchema } from "@/lib/breadcrumbs";
import { NearbyAirports } from "@/components/NearbyAirports";
import { nearbyAirports, nearbyAirportsItemList } from "@/lib/city-nearby-airports";
import { CityNearbyCities } from "@/components/CityNearbyCities";
import { CityHeroCta } from "@/components/CityHeroCta";
import { nearbyCities, nearbyCitiesItemList } from "@/lib/city-nearby-cities";
import { AnswerBlock } from "@/components/AnswerBlock";
import { cityAnswer } from "@/lib/answer-first";
import { CityWineCountry } from "@/components/CityWineCountry";
import { VideoShowcase } from "@/components/VideoShowcase";
import { CityPopularRoutes } from "@/components/CityPopularRoutes";
import { cityRoutes, cityRoutesItemList } from "@/lib/city-routes";

type CityLike = (typeof CITIES)[number];

// Region-tuned service priority — the top 4 services surfaced in the "Best services" module.
// Every service still appears in the full list; this just reorders the highlights per region.
type ServiceKey =
  | "airport-transfers" | "corporate" | "sprinter-groups" | "private-aviation"
  | "wine-tours" | "weddings" | "sporting-events";

// Nearest airport picker — drives the airport-transfer blurb per city
function nearestAirport(city: CityLike): { code: string; time: string } {
  const opts = [
    { code: "SFO", time: city.drive.sfo },
    { code: "SJC", time: city.drive.sjc },
    { code: "OAK", time: city.drive.oak },
  ];
  const toMin = (t: string) => {
    const m = t.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 999;
  };
  return opts.sort((a, b) => toMin(a.time) - toMin(b.time))[0];
}

// Region → nearest wine-country hub for the wine-tour blurb
const WINE_HUB: Record<string, string> = {
  "Silicon Valley": "Napa", "Peninsula": "Napa", "San Francisco": "Napa",
  "East Bay": "Livermore Valley & Napa", "Wine Country": "Napa & Sonoma",
  "Santa Cruz County": "Santa Cruz Mountains & Napa",
  "South Bay": "Santa Cruz Mountains & Napa",
  "Monterey County": "Carmel Valley & Napa",
  "Monterey Peninsula": "Carmel Valley & Napa",
};

// Region → likely sports/concert venue for the sporting-events blurb
const VENUE_HINT: Record<string, string> = {
  "Silicon Valley": "Levi's Stadium & SAP Center",
  "Peninsula": "Chase Center & Stanford Stadium",
  "San Francisco": "Chase Center & Oracle Park",
  "East Bay": "Chase Center & Oakland Arena",
  "Wine Country": "Chase Center & Shoreline",
  "Santa Cruz County": "SAP Center & Shoreline",
  "South Bay": "SAP Center & Levi's Stadium",
  "Monterey County": "Laguna Seca & SAP Center",
  "Monterey Peninsula": "Pebble Beach & Laguna Seca",
};

const SERVICE_META: Record<
  ServiceKey,
  { label: (city: string) => string; blurb: (city: CityLike) => string; icon: typeof Car }
> = {
  "airport-transfers": {
    label: () => "SFO, SJC, OAK & MRY airport transfers",
    blurb: (c) => {
      const n = nearestAirport(c);
      return `${c.name} → ${n.code} in ${n.time}. Flight-tracked, meet-and-greet on request.`;
    },
    icon: Plane,
  },
  "corporate": {
    label: (c) => `Corporate & executive travel in ${c}`,
    blurb: (c) => `Billed accounts for ${c.name} EAs — pre-arranged pickups to SFO (${c.drive.sfo}) & SJC (${c.drive.sjc}).`,
    icon: Briefcase,
  },
  "sprinter-groups": {
    label: () => "Mercedes Sprinter group transportation",
    blurb: (c) => `6–14 passengers from ${c.name} — roadshows, offsites, and airport shuttles across ${c.region}.`,
    icon: Users,
  },
  "private-aviation": {
    label: () => "Private aviation & FBO transfers",
    blurb: (c) => `Planeside pickup from ${c.name} to Signature SJC/SFO, Atlantic SJC & Monterey Jet Center.`,
    icon: Sparkles,
  },
  "wine-tours": {
    label: (c) => `Napa & Sonoma wine tours from ${c}`,
    blurb: (c) => `Full-day chauffeured tastings — ${c.name} to ${WINE_HUB[c.region] ?? "Napa & Sonoma"}, Suburban or Sprinter.`,
    icon: Wine,
  },
  "weddings": {
    label: () => "Weddings & special events",
    blurb: (c) => `${c.name} venues, hotels & receptions — sedan, SUV, and Sprinter packages with backup vehicles.`,
    icon: Heart,
  },
  "sporting-events": {
    label: () => "Sporting events, concerts & city tours",
    blurb: (c) => `${c.name} pickups to ${VENUE_HINT[c.region] ?? "Levi's, SAP & Chase Center"}.`,
    icon: Trophy,
  },
};


const REGION_PRIORITY: Record<string, ServiceKey[]> = {
  "Silicon Valley":      ["corporate", "airport-transfers", "sprinter-groups", "private-aviation"],
  "Peninsula":           ["corporate", "airport-transfers", "private-aviation", "sprinter-groups"],
  "San Francisco":       ["airport-transfers", "corporate", "sporting-events", "sprinter-groups"],
  "East Bay":            ["airport-transfers", "corporate", "wine-tours", "sprinter-groups"],
  "Wine Country":        ["wine-tours", "weddings", "airport-transfers", "sprinter-groups"],
  "Santa Cruz County":   ["weddings", "wine-tours", "airport-transfers", "sprinter-groups"],
  "South Bay":           ["airport-transfers", "corporate", "wine-tours", "sprinter-groups"],
  "Monterey County":     ["airport-transfers", "weddings", "wine-tours", "private-aviation"],
  "Monterey Peninsula":  ["weddings", "private-aviation", "airport-transfers", "wine-tours"],
};

const DEFAULT_PRIORITY: ServiceKey[] = ["airport-transfers", "corporate", "sprinter-groups", "private-aviation"];

// Region-tuned H1 suffix — mirrors the top "Best services" priority so the visible H1
// and meta title reinforce the same intent (corporate vs wine tour vs weddings, etc.).
// Every city in a region used to share ONE suffix, so ~8 Peninsula pages all
// published "<City> Executive Chauffeur & Black Car Service" and competed with
// each other (and with /service-areas/san-francisco) for the same phrase.
// Each region now carries several intent-equivalent suffixes and every city is
// pinned to one of them deterministically, so no two neighbouring city pages
// present Google with the same title pattern.
const REGION_H1_SUFFIXES: Record<string, string[]> = {
  "Silicon Valley":     ["Corporate & Executive Chauffeur Service", "Corporate Car Service & Airport Transfers", "Executive Car Service & SJC Transfers"],
  "Peninsula":          ["Executive Chauffeur & Black Car Service", "Airport Car Service & Private Chauffeur", "Luxury Sedan & SUV Chauffeur Service", "Black Car Service & SFO Transfers"],
  "San Francisco":      ["Airport Transfers & Black Car Service"],
  "East Bay":           ["Airport & Executive Chauffeur Service", "Corporate Car Service & Airport Transfers", "Private Chauffeur & Luxury SUV Service"],
  "Wine Country":       ["Napa & Sonoma Wine Tour Chauffeur Service", "Wine Tour Car Service & Airport Transfers"],
  "Santa Cruz County":  ["Wedding & Executive Chauffeur Service", "Coastal Wedding & Airport Car Service"],
  "South Bay":          ["Airport & Executive Chauffeur Service", "Group Sprinter & Corporate Car Service", "Private Chauffeur & SJC Transfers"],
  "Monterey County":    ["Airport & Private Aviation Chauffeur Service", "Private Aviation & Executive Car Service"],
  "Monterey Peninsula": ["Wedding, Private Aviation & Chauffeur Service", "Wedding & Private Aviation Car Service"],
};

// Round-robin the suffixes within each region so neighbouring cities never
// land on the same one. Computed once from the city list, so a city's title is
// stable across builds.
const CITY_SUFFIX: Record<string, string> = (() => {
  const counters: Record<string, number> = {};
  const out: Record<string, string> = {};
  for (const c of CITIES) {
    const options = REGION_H1_SUFFIXES[c.region];
    if (!options || options.length === 0) continue;
    const i = counters[c.region] ?? 0;
    counters[c.region] = i + 1;
    out[c.slug] = options[i % options.length];
  }
  return out;
})();

function citySuffix(city: CityLike): string {
  return CITY_SUFFIX[city.slug] ?? "Chauffeur & Black Car Service";
}

// Region-tuned H2 for the "Best services" module — mirrors the priority list.
const REGION_H2: Record<string, string> = {
  "Silicon Valley":     "Corporate travel & airport transfers, done right",
  "Peninsula":          "Executive travel & airport transfers for the Peninsula",
  "San Francisco":      "SFO, SJC & OAK airport transfers plus city black car",
  "East Bay":           "Airport transfers & corporate rides across the East Bay",
  "Wine Country":       "Napa & Sonoma wine tours, weddings & airport transfers",
  "Santa Cruz County":  "Weddings, wine tours & airport transfers on the coast",
  "South Bay":          "Airport transfers, corporate & group Sprinter service",
  "Monterey County":    "Airport, private aviation & wedding chauffeur service",
  "Monterey Peninsula": "Weddings, Pebble Beach & private aviation transfers",
};

// Region-tuned closing H2 (SEO/AEO copy block) — mirrors intent, adds nearest-airport drive time.
function seoCopyH2(city: CityLike): string {
  const n = nearestAirport(city);
  const suffix = citySuffix(city);
  return `${city.name} ${suffix.replace(" Service", "")} — ${n.code} in ${n.time}.`;
}

// Region-tuned fleet highlight
const REGION_FLEET_LEAD: Record<string, "sedan" | "suv" | "sprinter"> = {
  "Silicon Valley": "sedan",
  "Peninsula": "sedan",
  "San Francisco": "suv",
  "East Bay": "suv",
  "Wine Country": "sprinter",
  "Santa Cruz County": "suv",
  "South Bay": "suv",
  "Monterey County": "suv",
  "Monterey Peninsula": "sedan",
};

const FLEET_META = {
  sedan:    { name: "Lincoln Aviator midsize SUV (sedan-class service)", cap: "1–3 passengers", use: "Solo executives, VIP airport pickups" },
  suv:      { name: "Chevrolet Suburban LT SUV", cap: "up to 6 passengers", use: "Family, luggage-heavy runs, wine tours" },
  sprinter: { name: "Mercedes-Benz Sprinter executive van", cap: "6–14 passengers", use: "Corporate groups, weddings, roadshows" },
};



// City FAQs live in src/lib/city-faq.ts (imported above) so the visible
// section and the FAQPage JSON-LD always render the identical Q&A set.



// Nearby-city anchor texts now come from lib/city-nearby-cities (short + unique).


export const Route = createFileRoute("/service-areas/$city")({
  loader: ({ params }) => {
    const city = CITIES.find((c) => c.slug === params.city);
    if (!city) throw notFound();
    return { city };
  },
  head: ({ params, loaderData }) => {
    const city = loaderData?.city;
    if (!city) return { meta: [{ title: "City" }] };

    // Title: keep 50–60 chars. Region-tuned suffix mirrors the top "Best services" priority.
    const suffix = citySuffix(city);
    const near = nearestAirport(city);
    const titleLong = `${city.name} ${suffix} | ${near.code} in ${near.time}`;
    const titleMid = `${city.name} ${suffix} | ${near.code} ${near.time}`;
    const titleShort = `${city.name} ${suffix}`;
    const titleFallback = `${city.name} Chauffeur & Black Car Service`;
    const title =
      titleLong.length <= 60 ? titleLong :
      titleMid.length <= 60 ? titleMid :
      titleShort.length <= 60 ? titleShort :
      titleFallback;

    // Description: lead with nearest-airport drive-time intent (mirrors airport-transfers blurb),
    // then region-tuned service hook, then hotel/landmark, then CPUC credibility tail.
    const topHotel = city.hotels[0] ?? "";
    const topLandmark = city.landmarks[0] ?? "";
    const priorityForDesc = REGION_PRIORITY[city.region] ?? DEFAULT_PRIORITY;
    const leadService = SERVICE_META[priorityForDesc[0]].label(city.name).replace(/\.$/, "");
    const base = `${city.name} → ${near.code} in ${near.time}. ${leadService}: black car, SUV & Sprinter.`;
    const tails = [
      topHotel && topLandmark ? ` Serving ${topHotel} & ${topLandmark}. CPUC-licensed, insured, 24/7 — book online.` : "",
      topHotel && topLandmark ? ` Serving ${topHotel} & ${topLandmark}. CPUC-licensed & insured. Book 24/7.` : "",
      topHotel ? ` Serving ${topHotel}. Flight-tracked, CPUC-licensed & insured. Reserve online 24/7.` : "",
      topHotel ? ` Serving ${topHotel}. CPUC-licensed & insured chauffeurs, staffed dispatch.` : "",
      ` Flight-tracked, CPUC-licensed & insured chauffeurs on call daily — reserve online.`,
      ` CPUC-licensed & insured chauffeurs, flat airport rates, staffed dispatch.`,
      ` CPUC-licensed chauffeur service, available daily.`,
      ` CPUC-licensed.`,
    ].filter(Boolean);
    // Target the 150–160 character sweet spot: take the longest tail that fits.
    const description =
      base + (tails.filter((t) => (base + t).length <= 160).sort((a, b) => b.length - a.length)[0] ?? "");

    const canonical = `/service-areas/${params.city}`;
    const SITE_URL = "https://premierexecutivechauffeur.com";
    const absCanonical = `${SITE_URL}${canonical}`;

    // City-scoped Service, provided by the single address-free #business
    // Organization. Cities are service areas, never branch offices, so this
    // node carries no address or business coordinates.
    const localBusiness = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${absCanonical}#service`,
      name: `Chauffeur & black car service in ${city.name}, California`,
      serviceType: "Chauffeured black car, SUV and Sprinter van service",
      provider: { "@id": `${SITE_URL}#business` },
      brand: { "@type": "Brand", name: SITE.name },
      areaServed: {
        "@type": "City",
        name: city.name,
        containedInPlace: { "@type": "State", name: "California" },
      },
      url: absCanonical,
      image: imageObjectJsonLd({
        url: `${SITE_URL}/og/og-${city.slug}.jpg`,
        contentUrl: `${SITE_URL}/og/og-${city.slug}.jpg`,
        caption: `${city.name}, California chauffeur & black car service by Premier Executive Chauffeur`,
        description: `${city.name}, California chauffeur & black car service by Premier Executive Chauffeur — Lincoln Aviator, Suburban SUV & Sprinter`,
      }),
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
        opens: "00:00",
        closes: "23:59",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        servicePhone: { "@type": "ContactPoint", telephone: SITE.phone, contactType: "reservations" },
        serviceUrl: SITE.bookUrl,
      },
      description,
    };


    const breadcrumbs = breadcrumbListSchema([
      { name: "Home", path: "/" },
      { name: "Service Areas", path: "/service-areas" },
      { name: `${city.name}, ${city.region}`, path: absCanonical },
    ]);



    // Single consolidated FAQPage per URL: city Q&A + the visible pricing Q&A.
    const faqSchema = cityFaqSchema(
      city,
      absCanonical,
      buildCityAirportFaqs(city),
      // Only Q&A that is still visible on the page (medical detail moved to
      // /medical-transportation/$city, so its Q&A is emitted there instead).
      cityStaysFaqs(city).slice(0, 2),
    );

    // Nearby airports internal-link cluster (visible section + ItemList schema)
    const airportItems = nearbyAirports(city);
    const airportsList = nearbyAirportsItemList(city, absCanonical, airportItems);

    // Nearby cities internal-link cluster (visible section + ItemList schema)
    const citiesList = nearbyCitiesItemList(city, absCanonical, nearbyCities(city));

    // Popular point-to-point corridors (visible section + ItemList schema)
    const routesList = cityRoutesItemList(city, absCanonical, cityRoutes(city));



    const ogImage = `${SITE_URL}/og/og-${city.slug}.jpg`;
    const ogImageAlt = `${city.name}, California chauffeur & black car service by Premier Executive Chauffeur — Lincoln Aviator, Suburban SUV & Sprinter`;


    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: cityKeywords(city, "service-area") },
        { name: "geo.region", content: "US-CA" },
        { name: "geo.placename", content: `${city.name}, California` },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: absCanonical },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "og:image:alt", content: ogImageAlt },
        { property: "og:image:width", content: "1216" },
        { property: "og:image:height", content: "640" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: ogImageAlt },
      ],
      links: [
        { rel: "canonical", href: absCanonical },
        // LCP: the city hero photo is the largest above-the-fold paint.
        { rel: "preload", as: "image", href: `/og/og-${city.slug}.avif`, type: "image/avif", fetchpriority: "high" },
      ],

      scripts: [
        
        { type: "application/ld+json", children: ldJson(localBusiness) },
        { type: "application/ld+json", children: ldJson(breadcrumbs) },
        { type: "application/ld+json", children: ldJson(faqSchema) },
        { type: "application/ld+json", children: ldJson(airportsList) },
        { type: "application/ld+json", children: ldJson(citiesList) },
        { type: "application/ld+json", children: ldJson(routesList) },
        {
          type: "application/ld+json",
          children: ldJson(
            cityFleetImageSchema(city, nearestAirport(city).code, SITE_URL, absCanonical),
          ),
        },
        {
          type: "application/ld+json",
          children: ldJson({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${absCanonical}#webpage`,
            url: absCanonical,
            name: `${city.name} Chauffeur Service`,
            description,
            abstract: cityAnswer({
              name: city.name,
              region: city.region,
              airportCode: nearestAirport(city).code,
              driveTime: nearestAirport(city).time,
            }).answer,
            inLanguage: "en-US",
            isPartOf: { "@id": "https://premierexecutivechauffeur.com#website" },
            about: { "@id": `${absCanonical}#service` },
            breadcrumb: { "@id": `${absCanonical}#breadcrumb` },
            speakable: {
              "@type": "SpeakableSpecification",
              cssSelector: ["h1", ".answer-first"],
            },
          }),
        },

      ],
    };

  },
  component: CityPage,
  notFoundComponent: () => (
    <div className="container-x py-24 text-center">
      <h1 className="text-3xl mb-4">City not found</h1>
      <Link to="/service-areas" className="text-gold underline underline-offset-2">View all service areas</Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="container-x py-24 text-center">
      <h1 className="text-3xl mb-4">Something went wrong</h1>
      <p className="text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function CityPage() {
  const { city } = Route.useLoaderData();

  // Nearby cities: proximity-ranked from city centroids (see lib/city-nearby-cities).
  const nearby = nearbyCities(city);

  const priority = REGION_PRIORITY[city.region] ?? DEFAULT_PRIORITY;
  const fleetLead = REGION_FLEET_LEAD[city.region] ?? "sedan";
  const fleetOrder: ("sedan" | "suv" | "sprinter")[] = Array.from(new Set([fleetLead, "sedan", "suv", "sprinter"])) as any;



  const nearest = nearestAirport(city);
  const fleetHeroAlt = cityFleetImageCaption(city, nearest.code);
  const cityImage = `/og/og-${city.slug}.jpg`;
  const cityImageAlt = `${city.name}, California chauffeur & black car service by Premier Executive Chauffeur`;

  return (
    <>
      <section className="container-x py-20 md:py-28 max-w-4xl">
        <nav aria-label="Breadcrumb" className="eyebrow mb-4">
          <Link to="/" className="hover:text-gold-soft">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/service-areas" className="hover:text-gold-soft">All service areas</Link>
          <span className="mx-2">/</span>
          <span>{city.region} — {city.name}</span>
        </nav>
        <figure className="mb-8 border border-border overflow-hidden bg-surface">
          <picture>
            <source type="image/avif" srcSet={cityImage.replace(/\.jpg$/, ".avif")} />
            <source type="image/webp" srcSet={cityImage.replace(/\.jpg$/, ".webp")} />
            <img
              src={cityImage}
              alt={cityImageAlt}
              width={1216}
              height={640}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full h-auto block"
            />
          </picture>
        </figure>
        <h1 className="text-4xl md:text-6xl mb-6">
          {city.name} {citySuffix(city)} — {nearestAirport(city).code} in {nearestAirport(city).time}
        </h1>
        <p className="eyebrow mb-4 text-gold-soft">Flight-tracked · CPUC-licensed · Flat rates</p>

        <AnswerBlock
          className="mb-8"
          content={cityAnswer({
            name: city.name,
            region: city.region,
            airportCode: nearest.code,
            driveTime: nearest.time,
          })}
        />

        <p className="text-lg text-muted-foreground speakable">{city.intro}</p>
        <CityHeroCta city={city} airport={nearestAirport(city)} />

        <PageToc
          className="mt-10"
          qualifier={city.name}
          items={[
            { href: "#services", label: `Services in ${city.name}` },
            { href: "#nearby-airports", label: `Nearby airports` },
            { href: "#nearby-cities", label: `Nearby cities` },
            { href: "#hotels-destinations", label: `Hotels & destinations` },
            { href: "#local", label: `Local knowledge` },
            { href: "#instant-price", label: `Instant price` },
            { href: "#rates", label: `Hourly rates` },
            { href: "#why-us", label: `Why choose us` },
            { href: "#faq", label: `Chauffeur FAQ` },
          ]}
        />
      </section>


      {/* LOCAL BODY COPY — city-specific prose using landmarks, hotels, restaurants, transfers */}
      <section className="container-x pt-4 pb-4 max-w-3xl">
        <div className="space-y-4 text-muted-foreground">
          <p>
            Premier Executive Chauffeur runs pre-arranged black car service throughout {city.name} and the surrounding {city.region} corridor. Our chauffeurs know the residential streets, corporate driveways and loading zones — from {city.landmarks[0]}
            {city.landmarks[1] ? ` and ${city.landmarks[1]}` : ""}
            {city.landmarks[2] ? ` to ${city.landmarks[2]}` : ""}
            . Every ride is booked ahead, dispatched by a live team and driven by a CPUC-licensed chauffeur.
          </p>
          <p>
            Airport transfers lead the demand here: SFO is roughly {city.drive.sfo} away, SJC about {city.drive.sjc} and OAK around {city.drive.oak}, each flight-tracked so your chauffeur is staged before landing.
          </p>
        </div>

      </section>


      <section id="services" className="border-t border-border bg-surface">
        <div className="container-x py-16">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
            <div>
              <p className="eyebrow mb-2">Best services for {city.name}</p>
              <h2 className="text-2xl">{REGION_H2[city.region] ?? `Most-booked chauffeur services in ${city.region}`}</h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              Flat, all-in pricing on every {city.name} transfer — quoted before you confirm.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {priority.map((key) => {
              const meta = SERVICE_META[key];
              const Icon = meta.icon;
              return (
                <article
                  key={key}
                  className="border border-border bg-background p-5 hover:border-gold transition-colors"
                >
                  <Icon className="h-5 w-5 text-gold mb-3" />
                  <p className="font-display text-base leading-snug mb-1">
                    <Link
                      to="/services/$service"
                      params={{ service: key }}
                      className="hover:text-gold"
                    >
                      {meta.label(city.name)}
                    </Link>
                  </p>
                  <p className="text-xs text-muted-foreground">{meta.blurb(city)}</p>
                </article>
              );
            })}
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {fleetOrder.map((key) => {
              const f = FLEET_META[key];
              return (
                <article
                  key={key}
                  className="border border-border bg-background p-5 hover:border-gold transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Car className="h-4 w-4 text-gold" />
                    <span className="eyebrow">{key === fleetLead ? `Recommended for ${city.name}` : "Also available"}</span>
                  </div>
                  <p className="font-display text-base mb-1">
                    <Link to="/fleet" hash={key} className="hover:text-gold">
                      {f.name}
                    </Link>
                  </p>
                  <p className="text-xs text-muted-foreground">{f.cap} · {f.use}</p>
                </article>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground mt-6">
            Need something else? Browse{" "}
            <Link to="/services" className="text-gold underline underline-offset-2">all chauffeur services</Link>{" "}
            or the{" "}
            <Link to="/fleet" className="text-gold underline underline-offset-2">full fleet</Link>. Corporate travel managers can set up a billed account or get an instant price from the quote panel below.
          </p>
        </div>
      </section>



      {/* AIRPORT TRANSFERS — covered by the proximity-ranked NearbyAirports block below */}


      {/* NEARBY AIRPORTS — proximity-ranked internal links to /airports/$code */}
      <NearbyAirports city={city} items={nearbyAirports(city)} />

      <CityNearbyCities city={city} items={nearby} />

      {/* Service standards live on /services — linked, not duplicated per city. */}





      {/* POPULAR ROUTES — SF ↔ Peninsula / valley / coast corridors */}
      <CityPopularRoutes city={city} routes={cityRoutes(city)} />

      {/* HOTELS & DESTINATIONS — per-city stays, destinations and booking FAQ */}
      <CityStaysDestinations city={city} />

      {/* LOCAL WINE COUNTRY — nearest appellation, named tasting rooms */}
      <CityWineCountry city={city} />

      {/* MEDICAL APPOINTMENTS — full detail lives on the dedicated city page */}
      <section className="container-x py-6">
        <p className="text-sm text-muted-foreground max-w-3xl">
          Need a ride to a clinic, infusion centre or hospital discharge?{" "}
          <Link
            to="/medical-transportation/$city"
            params={{ city: city.slug }}
            className="text-gold underline underline-offset-2"
          >
            {city.name} medical appointment transportation
            <span className="sr-only"> — facilities, rates and booking</span>
          </Link>{" "}
          covers facilities, wait time and pricing (non-emergency, non-ambulance).
        </p>
      </section>

      {/* BRAND VIDEO — click-to-load facade, VideoObject emitted in head */}
      <VideoShowcase
        heading={`See the ${city.name} fleet before you book`}
        blurb={`The same Lincoln Aviator midsize SUVs used for sedan-class service, Chevrolet Suburban SUVs and Mercedes-Benz Sprinter vans that run ${city.name} airport transfers, corporate travel, wine tours and medical appointments — with the chauffeur standard we hold on every trip.`}
        playLabel={`Play the Premier Executive Chauffeur fleet video for ${city.name}`}
        posterAlt={`${city.name}, CA chauffeur fleet — black Lincoln Aviator midsize SUV (sedan-class service), Chevrolet Suburban SUV and Mercedes-Benz Sprinter van with uniformed chauffeurs serving ${city.region}`}
      />

      {/* LOCAL CONTEXT — reusable city SEO block */}
      <CityLocalBlock city={city} />

      {/* HOURLY RATES */}
      <InstantPriceWidget
        defaultPickup={`${city.name}, CA`}
        heading={`Instant price from ${city.name}`}
        intro={`Point-to-point and city-to-city one-way transfers from ${city.name} are quoted as flat all-in rates with no hourly minimum. Enter your trip for an instant price, or send it to dispatch on WhatsApp.`}
      />

      <HourlyRates
        heading={`${city.name} hourly as-directed rates`}
        intro={`Published hourly pricing for ${city.name} and the surrounding ${city.region} area. Every as-directed booking carries a five-hour minimum; tolls, parking, airport fees, and gratuity treatment are shown in writing, with gratuity excluded unless confirmed otherwise.`}
        showFaqs={false}
      />


      {/* TESTIMONIALS */}
      <LocalMap
        cityName={city.name}
        coords={CITY_COORDS[city.slug]}
        heading={`${city.name} black car & limo service area`}
        intro={`${city.name} pickups are dispatched from our Sunnyvale base — the same verified address, phone and hours listed on our Google Business Profile, so local searches for black car, limousine and chauffeur service in ${city.region} resolve to one consistent business.`}
      />




      {/* Fleet lineup image — placed just before the "Why … chooses us" block */}
      <section className="border-t border-border">
        <div className="container-x py-12 max-w-5xl">
          <figure className="border border-border overflow-hidden bg-surface">
            <picture>
              <source type="image/avif" srcSet={CITY_FLEET_IMAGE_PATH.replace(/\.jpg$/, ".avif")} />
              <source type="image/webp" srcSet={CITY_FLEET_IMAGE_PATH.replace(/\.jpg$/, ".webp")} />
              <img
                src={CITY_FLEET_IMAGE_PATH}
                alt={fleetHeroAlt}
                width={CITY_FLEET_IMAGE_WIDTH}
                height={CITY_FLEET_IMAGE_HEIGHT}
                sizes="(min-width: 1024px) 960px, 100vw"
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
            </picture>
            <figcaption className="sr-only">{fleetHeroAlt}</figcaption>
          </figure>
        </div>
      </section>

      {/* SEO/AEO copy block */}
      <section id="why-us" className="border-t border-border bg-surface">
        <div className="container-x py-16 max-w-3xl">
          <p className="eyebrow mb-4">Why {city.name} chooses us</p>
          <h2 className="text-3xl mb-5">{seoCopyH2(city)}</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Premier Executive Chauffeur — a division of {SITE.parent} — moves executives, private aviation guests, wedding parties and corporate groups across {city.region}. In {city.name}, we are the choice of executive assistants who need a chauffeur there on time, every time.
            </p>
            <p>
              Every {city.name} assignment is dispatched with flight tracking, pre-arranged pricing and a fully insured, TCP-permitted vehicle regulated by the {SITE.regulator}. Use the instant price panel above to reserve, or ask dispatch about a corporate account.
            </p>
          </div>


          <div className="mt-10">
            <div className="border border-border p-6">
              <p className="eyebrow mb-3">Popular {city.name} services</p>
              <ul className="text-sm space-y-2 sm:grid sm:grid-cols-2 sm:gap-2 sm:space-y-0">
                <li><Link to="/services/$service" params={{ service: "airport-transfers" }} className="hover:text-gold">{city.name} airport transfers</Link></li>
                <li><Link to="/services/$service" params={{ service: "corporate" }} className="hover:text-gold">{city.name} corporate travel</Link></li>
                <li><Link to="/services/$service" params={{ service: "sprinter-groups" }} className="hover:text-gold">{city.name} Sprinter groups</Link></li>
                <li><Link to="/services/$service" params={{ service: "private-aviation" }} className="hover:text-gold">{city.name} private aviation</Link></li>
                <li><Link to="/services/$service" params={{ service: "weddings" }} className="hover:text-gold">{city.name} weddings</Link></li>
                <li><Link to="/fleet" className="hover:text-gold">{city.name} fleet options</Link></li>
              </ul>
            </div>
          </div>

        </div>
      </section>

      {/* FAQ — matches FAQPage JSON-LD in <head> */}
      <section id="faq" className="border-t border-border">
        <div className="container-x py-20 max-w-3xl speakable">
          <p className="eyebrow mb-4">FAQ</p>
          <h2 className="text-3xl mb-8">{city.name} chauffeur service — frequently asked questions</h2>
          <div className="divide-y divide-border">
            {cityFaqList(city, buildCityAirportFaqs(city)).map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none flex justify-between items-start gap-4 text-lg hover:text-gold">
                  <span>{f.q}</span>
                  <span className="text-gold text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-muted-foreground mt-3 text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>


      {LIMO_SLUGS.includes(city.slug) && (
        <section className="container-x pb-4">
          <p className="text-sm text-muted-foreground">
            Searching for a limousine specifically?{" "}
            <Link to="/limo-service/$city" params={{ city: city.slug }} className="text-gold underline underline-offset-2 hover:text-gold-soft">
              Limo Service {city.name}
              <span className="sr-only"> — commercial landing page</span>
            </Link>{" "}
            covers pricing, vehicles, and airport corridors. Groups of six to fourteen should start with{" "}
            <Link to="/sprinter-van-transportation" className="text-gold underline underline-offset-2 hover:text-gold-soft">
              Sprinter van transportation
              <span className="sr-only"> — from the {city.name} page</span>
            </Link>
            .
          </p>
        </section>
      )}

      <CrossLinkModule context={{ type: "city", slug: city.slug }} showFaqs={false} />


      <CTABand
        heading={`Reserve your ${city.name} chauffeur`}
        sub={`SFO in ${city.drive.sfo}. SJC in ${city.drive.sjc}. OAK in ${city.drive.oak}. Book online or call ${SITE.phone}.`}
      />
    </>
  );
}
